import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { RefreshCw, Package, CarFront } from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';
import { collection, query, where, getDocs, getDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { requestDriverLocations } from '../services/driverService';
import 'leaflet/dist/leaflet.css';

// Platform icons SVG paths
const AndroidIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#3DDC84">
    <path d="M17.523 15.341c-.5 0-.906-.406-.906-.906s.406-.906.906-.906.906.406.906.906-.406.906-.906.906zm-11.046 0c-.5 0-.906-.406-.906-.906s.406-.906.906-.906.906.406.906.906-.406.906-.906.906zm11.4-6.839l1.932-3.346c.108-.187.044-.427-.143-.535-.187-.108-.427-.044-.535.143l-1.958 3.39c-1.48-.674-3.14-1.054-4.93-1.054s-3.45.38-4.93 1.054l-1.958-3.39c-.108-.187-.348-.251-.535-.143-.187.108-.251.348-.143.535l1.932 3.346c-3.165 1.715-5.285 4.89-5.285 8.498h21.75c0-3.608-2.12-6.783-5.285-8.498z" />
  </svg>
);

const AppleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#555555">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Professional driver icon with CarFront from Lucide
const createDriverIcon = () => {
  const iconMarkup = renderToStaticMarkup(
    <div style={{ position: 'relative' }}>
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '3px solid white',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 2px 4px rgba(0,0,0,0.2)',
        position: 'relative',
      }}>
        <CarFront size={24} color="white" strokeWidth={2.5} />
      </div>
      <div style={{
        position: 'absolute',
        bottom: '-8px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '0',
        height: '0',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: '8px solid white',
        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))',
      }}></div>
    </div>
  );

  return L.divIcon({
    html: iconMarkup,
    className: 'custom-driver-marker',
    iconSize: [48, 56],
    iconAnchor: [24, 56],
    popupAnchor: [0, -56]
  });
};

const driverIcon = createDriverIcon();

// Create order icon with circle background and package icon
const createOrderIcon = (color, bgColor) => {
  const iconMarkup = renderToStaticMarkup(
    <div style={{ position: 'relative' }}>
      <div style={{
        backgroundColor: bgColor,
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '3px solid white',
        boxShadow: `0 4px 12px ${bgColor}66, 0 2px 4px rgba(0,0,0,0.2)`,
        position: 'relative',
      }}>
        <Package size={22} color="white" strokeWidth={2.5} />
      </div>
      <div style={{
        position: 'absolute',
        bottom: '-8px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '0',
        height: '0',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: '8px solid white',
        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))',
      }}></div>
    </div>
  );

  return L.divIcon({
    html: iconMarkup,
    className: 'custom-order-marker',
    iconSize: [44, 52],
    iconAnchor: [22, 52],
    popupAnchor: [0, -52]
  });
};

const statusIcons = {
  waiting: createOrderIcon('yellow', '#eab308'),
  accepted: createOrderIcon('blue', '#3b82f6'),
  in_progress: createOrderIcon('orange', '#f97316'),
  returned_pending: createOrderIcon('violet', '#a855f7')
};

const statusColors = {
  waiting: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Waiting', color: '#eab308' },
  accepted: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Accepted', color: '#3b82f6' },
  in_progress: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'In Progress', color: '#f97316' },
  returned_pending: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Return Pending', color: '#a855f7' }
};

// Auto-center map to show all markers
function MapController({ drivers, orders }) {
  const map = useMap();
  const hasFittedRef = useRef(false);

  useEffect(() => {
    // Only fit-bounds once, on the very first non-empty snapshot. After
    // that we leave the user's zoom/pan alone — subsequent realtime
    // updates just move markers, they shouldn't reset the view.
    if (hasFittedRef.current) return;

    const allPoints = [];
    Object.values(drivers).forEach((driver) => {
      if (driver.isOnline && driver.latitude && driver.longitude) {
        allPoints.push([driver.latitude, driver.longitude]);
      }
    });
    orders.forEach((order) => {
      if (order.startPlace?.lat && order.startPlace?.lng) {
        allPoints.push([order.startPlace.lat, order.startPlace.lng]);
      }
    });

    if (allPoints.length > 0) {
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [50, 50] });
      hasFittedRef.current = true;
    }
  }, [drivers, orders, map]);

  return null;
}

// Subscribe to online drivers from Firestore users collection (real-time)
const subscribeToOnlineDrivers = (setDrivers) => {
  const usersRef = collection(db, 'users');
  const onlineDriversQuery = query(
    usersRef,
    where('type', '==', 'driver'),
    where('isOnline', '==', true)
  );

  const unsubscribe = onSnapshot(onlineDriversQuery, (snapshot) => {
    const driversData = {};

    snapshot.forEach((doc) => {
      const data = doc.data();

      // Extract latitude/longitude - check multiple possible locations
      let latitude = null;
      let longitude = null;

      // Primary: Direct fields at root level
      if (data.latitude && data.longitude) {
        latitude = data.latitude;
        longitude = data.longitude;
      }

      // Fallback: driverLocation.geopoint (GeoPoint format)
      if (!latitude && data.driverLocation?.geopoint) {
        const geopoint = data.driverLocation.geopoint;
        latitude = geopoint.latitude ?? geopoint._lat;
        longitude = geopoint.longitude ?? geopoint._long;
      }

      // Convert Firestore timestamp to milliseconds
      let lastUpdated = null;
      if (data.driverLocation?.lastUpdate) {
        lastUpdated = data.driverLocation.lastUpdate.toMillis
          ? data.driverLocation.lastUpdate.toMillis()
          : data.driverLocation.lastUpdate;
      } else if (data.lastLocationUpdate) {
        lastUpdated = data.lastLocationUpdate.toMillis
          ? data.lastLocationUpdate.toMillis()
          : data.lastLocationUpdate;
      }

      driversData[doc.id] = {
        driverId: data.uid || doc.id,
        name: data.name || 'Unknown',
        latitude,
        longitude,
        lastUpdated,
        platform: data.platform || 'unknown',
        fcmToken: data.fcmToken,
        phoneNumber: data.phoneNumber,
        email: data.email || null,
        profilePhoto: data.profilePhoto || data.photoUrl || null,
        walletBalance: typeof data.wallet_balance === 'number'
          ? data.wallet_balance
          : (typeof data.walletBalance === 'number' ? data.walletBalance : null),
        status: data.status || null,
        rating: typeof data.rating === 'number' ? data.rating : null,
        isOnline: true,
      };
    });

    console.log('Online drivers updated:', Object.keys(driversData).length);
    setDrivers(driversData);
  }, (error) => {
    console.error('Error listening to online drivers:', error);
  });

  return unsubscribe;
};
// Real-time subscription to active orders. Returns the unsubscribe fn.
const subscribeToActiveOrders = (setOrders) => {
  const ordersRef = collection(db, 'orders');
  const activeStatuses = ['waiting', 'accepted', 'in_progress', 'returned_pending'];
  const q = query(ordersRef, where('status', 'in', activeStatuses));
  return onSnapshot(
    q,
    (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setOrders(list);
    },
    (error) => {
      console.error('Error in active orders listener:', error);
    },
  );
};

const fetchActiveOrders = async () => {
  try {
    const ordersRef = collection(db, 'orders');
    const statuses = ['waiting', 'accepted', 'in_progress', 'returned_pending'];
    const allOrders = [];

    for (const status of statuses) {
      const q = query(ordersRef, where('status', '==', status));
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        allOrders.push({
          id: doc.id,
          ...data
        });
      });
    }

    console.log('Fetched orders:', allOrders.length);
    return allOrders;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export default function MapTracking() {
  const [drivers, setDrivers] = useState({});
  // Lazy lookup cache for *offline* drivers assigned to active orders.
  // The `drivers` map only contains online drivers (the map listener
  // filters by isOnline:true so we don't pull every driver doc every
  // few seconds), so when an assigned driver goes offline mid-trip the
  // popup loses their name/phone. We fetch each missing uid once and
  // cache it here. Keys are uids; values are {name, phoneNumber, ...}
  // shaped like the online entries.
  const [offlineDriverCache, setOfflineDriverCache] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showDrivers, setShowDrivers] = useState(true);
  const [showOrders, setShowOrders] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [mapCenter] = useState([31.9539, 35.9106]); // Amman, Jordan

  // Pagination state
  const [visibleDriversCount, setVisibleDriversCount] = useState(10);
  const [visibleOrdersCount, setVisibleOrdersCount] = useState(10);
  const scrollContainerRef = useRef(null);
  const mapRef = useRef(null);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    setLoading(true);

    // Real-time subscriptions for drivers and orders.
    const unsubscribeDrivers = subscribeToOnlineDrivers(setDrivers);
    const unsubscribeOrders = subscribeToActiveOrders((list) => {
      setOrders(list);
      setLastRefresh(new Date());
      setLoading(false);
    });

    // Safety net in case the orders listener takes a while to fire even
    // when the collection is empty.
    const loadingTimer = setTimeout(() => setLoading(false), 4000);

    return () => {
      clearTimeout(loadingTimer);
      if (unsubscribeDrivers) unsubscribeDrivers();
      if (unsubscribeOrders) unsubscribeOrders();
    };
  }, []);

  // Auto-fetch any assigned driver that's NOT in the online drivers map.
  // Runs whenever the order list or the online drivers map changes.
  // We dedupe against both `drivers` (online) and `offlineDriverCache`
  // so each missing uid is fetched at most once per session.
  useEffect(() => {
    const missing = new Set();
    for (const o of orders) {
      const uid = o.driverId;
      if (!uid) continue;
      if (drivers[uid]) continue;
      if (offlineDriverCache[uid]) continue;
      missing.add(uid);
    }
    if (missing.size === 0) return;

    let cancelled = false;
    (async () => {
      const fetched = {};
      await Promise.all(
        Array.from(missing).map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, 'users', uid));
            if (!snap.exists()) return;
            const data = snap.data();
            fetched[uid] = {
              driverId: uid,
              name: data.name || 'Unknown',
              phoneNumber: data.phoneNumber || null,
              email: data.email || null,
              profilePhoto: data.profilePhoto || data.photoUrl || null,
              platform: data.platform || 'unknown',
              status: data.status || null,
              rating: typeof data.rating === 'number' ? data.rating : null,
              isOnline: false,
              // No live location for offline drivers — popup only needs
              // identity, so we don't bother reading the stale geopoint.
              latitude: null,
              longitude: null,
            };
          } catch (e) {
            console.warn(`Failed to fetch offline driver ${uid}:`, e);
          }
        }),
      );
      if (cancelled || Object.keys(fetched).length === 0) return;
      setOfflineDriverCache((prev) => ({ ...prev, ...fetched }));
    })();

    return () => {
      cancelled = true;
    };
  }, [orders, drivers, offlineDriverCache]);

  // Resolve a driverId to whichever entry we have — online listener wins,
  // offline cache as fallback.
  const lookupDriver = useCallback(
    (uid) => (uid ? drivers[uid] || offlineDriverCache[uid] || null : null),
    [drivers, offlineDriverCache],
  );

  const handleRefresh = async () => {
    try {
      setRefreshing(true);

      // Step 1: Call Cloud Function to request driver locations via FCM
      console.log('Requesting driver locations via FCM...');
      try {
        const fcmResult = await requestDriverLocations();
        console.log('FCM result:', fcmResult);

        // Step 2: Wait 3 seconds for drivers to respond and update Firestore
        if (fcmResult.count > 0) {
          console.log(`Waiting 3s for ${fcmResult.count} drivers to respond...`);
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      } catch (fcmError) {
        console.warn('FCM notification failed (continuing with refresh):', fcmError.message);
      }

      // Orders are now real-time via Firestore subscription; just stamp
      // the last-refresh timestamp so the UI feels responsive.
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error refreshing data:', error);
      alert('Error refreshing map data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  // Handle scroll for infinite pagination
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom) {
      // Load more drivers if needed
      if (showDrivers && visibleDriversCount < Object.keys(drivers).length) {
        setVisibleDriversCount(prev => Math.min(prev + ITEMS_PER_PAGE, Object.keys(drivers).length));
      }
      // Load more orders if needed
      if (showOrders && visibleOrdersCount < orders.length) {
        setVisibleOrdersCount(prev => Math.min(prev + ITEMS_PER_PAGE, orders.length));
      }
    }
  }, [showDrivers, showOrders, visibleDriversCount, visibleOrdersCount, drivers, orders]);

  // Reset visible counts when filter changes
  useEffect(() => {
    setVisibleOrdersCount(10);
  }, [filterStatus]);

  // Handle click on driver in sidebar - fly to location on map
  const handleDriverClick = (id, driver) => {
    setSelectedItem(`driver-${id}`);
    if (mapRef.current && driver.latitude && driver.longitude) {
      mapRef.current.flyTo([driver.latitude, driver.longitude], 15, {
        duration: 1
      });
    }
  };

  // Handle click on order in sidebar - fly to location on map
  const handleOrderClick = (order) => {
    setSelectedItem(`order-${order.id}`);
    if (mapRef.current && order.startPlace?.lat && order.startPlace?.lng) {
      mapRef.current.flyTo([order.startPlace.lat, order.startPlace.lng], 15, {
        duration: 1
      });
    }
  };

  const onlineDrivers = Object.entries(drivers).filter(([_, driver]) => driver.isOnline);
  const offlineDrivers = Object.entries(drivers).filter(([_, driver]) => !driver.isOnline);

  const filteredOrders = (filterStatus === 'all'
    ? orders
    : orders.filter(o => o.status === filterStatus)
  ).sort((a, b) => {
    // Sort by createdAt descending (newest first)
    const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
    const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
    return timeB - timeA;
  });

  // Paginated arrays for display in sidebar
  const paginatedDrivers = onlineDrivers.slice(0, visibleDriversCount);
  const paginatedOrders = filteredOrders.slice(0, visibleOrdersCount);

  // Helper to format order number with subNumber
  const formatOrderNumber = (order) => {
    if (order.subNumber) {
      return `#${order.orderNumber}-${order.subNumber}`;
    }
    return `#${order.orderNumber}`;
  };

  const ordersByStatus = {
    waiting: orders.filter(o => o.status === 'waiting').length,
    accepted: orders.filter(o => o.status === 'accepted').length,
    in_progress: orders.filter(o => o.status === 'in_progress').length,
    returned_pending: orders.filter(o => o.status === 'returned_pending').length,
  };

  // Human "how long ago" label. Two things to fix vs. the naive impl:
  //   1. Clamp elapsed at 0 — a slightly-behind admin clock could
  //      otherwise render '-3s ago'.
  //   2. Cap the units — nobody wants to read '144h ago'. Above 24h
  //      we say "Yesterday" / "Nd ago" / "Long time ago" instead.
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const elapsedMs = Math.max(0, Date.now() - timestamp);
    const seconds = Math.floor(elapsedMs / 1000);
    if (seconds < 30) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return 'Long time ago';
  };

  // Tier used for coloring. Green = fresh, amber = idle, red = stale.
  // The Cloud Function purges > 3 min drivers, so anyone showing red
  // is either mid-purge or a fresh signal that just arrived stale.
  const freshnessTier = (timestamp) => {
    if (!timestamp) return 'red';
    const elapsedMs = Math.max(0, Date.now() - timestamp);
    if (elapsedMs < 60_000) return 'green'; // < 1 min
    if (elapsedMs < 5 * 60_000) return 'amber'; // < 5 min
    return 'red';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatLastRefresh = () => {
    if (!lastRefresh) return '';
    return lastRefresh.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading map data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <div className="w-96 bg-white shadow-lg flex flex-col overflow-hidden flex-shrink-0">
        <div className="p-6 border-b flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-2xl font-bold text-gray-800">Map Tracking</h1>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`p-2 rounded-lg transition-all ${refreshing
                ? 'bg-gray-200 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              title="Refresh data"
            >
              <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>

          {lastRefresh && (
            <p className="text-xs text-gray-500 mb-2">
              Last updated: {formatLastRefresh()}
            </p>
          )}


          {/* Toggle Filters */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setShowDrivers(!showDrivers)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${showDrivers
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <CarFront size={16} className="inline mr-1" />
              Drivers ({onlineDrivers.length})
            </button>
            <button
              onClick={() => setShowOrders(!showOrders)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${showOrders
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <Package size={16} className="inline mr-1" />
              Orders ({orders.length})
            </button>
          </div>

          {/* Order Status Filter */}
          {showOrders && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-600 uppercase">Filter Orders</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses ({orders.length})</option>
                <option value="waiting">Waiting ({ordersByStatus.waiting})</option>
                <option value="accepted">Accepted ({ordersByStatus.accepted})</option>
                <option value="in_progress">In Progress ({ordersByStatus.in_progress})</option>
                <option value="returned_pending">Return Pending ({ordersByStatus.returned_pending})</option>

              </select>
            </div>
          )}
        </div>

        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="p-4 overflow-y-auto flex-1"
          style={{ maxHeight: 'calc(100vh - 300px)' }}
        >
          {/* Online Drivers */}
          {showDrivers && onlineDrivers.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-green-600 mb-3 uppercase flex items-center">
                <CarFront size={16} className="mr-1" />
                Online Drivers ({paginatedDrivers.length}/{onlineDrivers.length})
              </h2>
              <div className="space-y-2">
                {paginatedDrivers.map(([id, driver]) => {
                  const tier = freshnessTier(driver.lastUpdated);
                  const dotColor =
                    tier === 'green' ? 'bg-green-500'
                    : tier === 'amber' ? 'bg-amber-500'
                    : 'bg-red-500';
                  const timeColor =
                    tier === 'green' ? 'text-green-700'
                    : tier === 'amber' ? 'text-amber-700'
                    : 'text-red-700';
                  return (
                    <div
                      key={`driver-${id}`}
                      onClick={() => handleDriverClick(id, driver)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedItem === `driver-${id}`
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300 bg-white'
                        }`}
                    >
                      {/* Row 1: name (truncates) + platform badge. `min-w-0`
                          on the name column lets `truncate` actually kick in
                          inside the flex row — without it the name would
                          keep expanding and push everything off screen. */}
                      <div className="flex items-center gap-2">
                        <h3
                          className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0"
                          title={driver.name || 'Unknown Driver'}
                        >
                          {driver.name || 'Unknown Driver'}
                        </h3>
                        {driver.platform && (
                          <span className="flex items-center shrink-0" title={driver.platform}>
                            {driver.platform === 'android' ? <AndroidIcon /> : driver.platform === 'ios' ? <AppleIcon /> : null}
                          </span>
                        )}
                      </div>
                      {/* Row 2: freshness pill — always visible regardless
                          of name length. Colored dot signals live/idle/stale
                          at a glance. */}
                      <div className={`mt-1.5 inline-flex items-center gap-1.5 text-xs ${timeColor}`}>
                        <span className={`w-2 h-2 rounded-full ${dotColor} ${tier === 'green' ? 'animate-pulse' : ''}`}></span>
                        <span>{formatTime(driver.lastUpdated)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Load more indicator for drivers */}
              {visibleDriversCount < onlineDrivers.length && (
                <div className="text-center py-2 text-xs text-gray-500">
                  Scroll for more... ({onlineDrivers.length - visibleDriversCount} remaining)
                </div>
              )}
            </div>
          )}

          {/* Orders */}
          {showOrders && filteredOrders.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-blue-600 mb-3 uppercase flex items-center">
                <Package size={16} className="mr-1" />
                Active Orders ({paginatedOrders.length}/{filteredOrders.length})
              </h2>
              <div className="space-y-2">
                {paginatedOrders.map((order) => {
                  const statusStyle = statusColors[order.status] || statusColors.waiting;
                  return (
                    <div
                      key={`order-${order.id}`}
                      onClick={() => handleOrderClick(order)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedItem === `order-${order.id}`
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-white'
                        }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-semibold text-gray-800 text-sm">Order {formatOrderNumber(order)}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                          {statusStyle.label}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 space-y-0.5">
                        <p><span className="font-medium">From:</span> {order.startPlace?.city || 'N/A'}</p>
                        <p><span className="font-medium">To:</span> {order.endPlace?.city || 'N/A'}</p>
                        <p><span className="font-medium">Price:</span> {order.totalPrice} DA</p>
                      </div>
                      {(!order.startPlace?.lat || !order.startPlace?.lng) && (
                        <div className="mt-1 text-xs text-red-600">⚠️ No location</div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Load more indicator for orders */}
              {visibleOrdersCount < filteredOrders.length && (
                <div className="text-center py-2 text-xs text-gray-500">
                  Scroll for more... ({filteredOrders.length - visibleOrdersCount} remaining)
                </div>
              )}
            </div>
          )}

          {showDrivers && onlineDrivers.length === 0 && showOrders && filteredOrders.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p className="mb-2">No data to display</p>
              <button
                onClick={handleRefresh}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                Refresh to check again
              </button>
            </div>
          )}
        </div>
      </div >

      {/* Map */}
      < div className="flex-1 relative overflow-hidden" >
        <MapContainer
          center={mapCenter}
          zoom={8}
          className="h-full w-full"
          scrollWheelZoom={true}
          doubleClickZoom={true}
          dragging={true}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapController drivers={drivers} orders={orders} />

          {/* Order Markers - Rendered first so they appear below drivers */}
          {showOrders && filteredOrders.map((order) => {
            if (!order.startPlace?.lat || !order.startPlace?.lng) return null;

            const statusStyle = statusColors[order.status] || statusColors.waiting;
            const icon = statusIcons[order.status] || statusIcons.waiting;

            return (
              <Marker
                key={`order-${order.id}`}
                position={[order.startPlace.lat, order.startPlace.lng]}
                icon={icon}
                zIndexOffset={0}
              >
                <Popup minWidth={260} maxWidth={320}>
                  {(() => {
                    const assignedDriver = lookupDriver(order.driverId);
                    const driverName = assignedDriver?.name || null;
                    const driverPhone = assignedDriver?.phoneNumber || null;
                    const customerPhone = order.customerPhone || order.senderNumber || null;
                    const isActive = ['accepted', 'in_progress', 'returned_pending'].includes(order.status);
                    const pickupText = [order.startPlace?.area, order.startPlace?.city]
                      .filter(Boolean)
                      .join(', ') || order.startPlace?.address || 'N/A';
                    const deliveryText = [order.endPlace?.area, order.endPlace?.city]
                      .filter(Boolean)
                      .join(', ') || order.endPlace?.address || 'N/A';

                    return (
                  <div className="p-1" style={{ minWidth: 240 }}>
                    {/* Header: order number + status pill */}
                    <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                      <h3 className="font-bold text-gray-800 text-sm">
                        Order {formatOrderNumber(order)}
                      </h3>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${statusStyle.bg} ${statusStyle.text}`}
                      >
                        {statusStyle.label}
                      </span>
                    </div>

                    {/* Route — stacked Start / End for readability */}
                    <div className="pt-2 space-y-1 text-xs">
                      <div>
                        <span className="text-gray-500">Start destination: </span>
                        <span className="font-semibold text-gray-800">{pickupText}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">End destination: </span>
                        <span className="font-semibold text-gray-800">{deliveryText}</span>
                      </div>
                    </div>

                    {/* Customer */}
                    {(order.customerName || order.senderName || customerPhone) && (
                      <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-xs">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">
                          Customer
                        </p>
                        {(order.customerName || order.senderName) && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Name</span>
                            <span className="font-semibold text-gray-800 truncate">
                              {order.customerName || order.senderName}
                            </span>
                          </div>
                        )}
                        {customerPhone && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Phone</span>
                            <a
                              href={`tel:${customerPhone}`}
                              className="font-mono font-semibold text-blue-600 hover:underline truncate"
                            >
                              {customerPhone}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Driver */}
                    {isActive && order.driverId && (
                      <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-xs">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">
                          Driver
                        </p>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500">Name</span>
                          <span className="font-semibold text-gray-800 truncate">
                            {driverName || `${order.driverId.slice(0, 8)}…`}
                          </span>
                        </div>
                        {driverPhone && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Phone</span>
                            <a
                              href={`tel:${driverPhone}`}
                              className="font-mono font-semibold text-blue-600 hover:underline truncate"
                            >
                              {driverPhone}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Prices */}
                    {(order.itemPrice != null || order.deliveryPrice != null || order.totalPrice != null) && (
                      <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-xs">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">
                          Prices
                        </p>
                        {order.itemPrice != null && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Item</span>
                            <span className="font-semibold text-gray-800">
                              {Number(order.itemPrice).toFixed(2)} JOD
                            </span>
                          </div>
                        )}
                        {order.deliveryPrice != null && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Delivery</span>
                            <span className="font-semibold text-gray-800">
                              {Number(order.deliveryPrice).toFixed(2)} JOD
                            </span>
                          </div>
                        )}
                        {order.totalPrice != null && (
                          <div className="flex justify-between gap-2 pt-0.5 border-t border-dashed border-gray-200">
                            <span className="text-gray-500 font-semibold">Total</span>
                            <span className="font-bold text-gray-900">
                              {Number(order.totalPrice).toFixed(2)} JOD
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Created at */}
                    {order.createdAt && (
                      <div className="mt-2 pt-2 border-t border-gray-200 text-xs">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500">Created at</span>
                          <span className="text-gray-700">{formatDate(order.createdAt)}</span>
                        </div>
                      </div>
                    )}

                    {/* Location actions */}
                    <div className="mt-2 pt-2 border-t border-gray-200 flex gap-2">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${order.startPlace.lat},${order.startPlace.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-2 py-1.5 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 transition"
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg"
                          alt="Google Maps"
                          className="w-5 h-5"
                        />
                        <span className="text-white">Google Map</span>
                      </a>
                      <button
                        type="button"
                        onClick={async (e) => {
                          const coords = `${order.startPlace.lat},${order.startPlace.lng}`;
                          const btn = e.currentTarget;
                          const original = btn.textContent;
                          try {
                            await navigator.clipboard.writeText(coords);
                            btn.textContent = 'Copied!';
                          } catch {
                            const ta = document.createElement('textarea');
                            ta.value = coords;
                            document.body.appendChild(ta);
                            ta.select();
                            try { document.execCommand('copy'); } catch (_) {}
                            document.body.removeChild(ta);
                            btn.textContent = 'Copied!';
                          }
                          setTimeout(() => { btn.textContent = original; }, 1500);
                        }}
                        className="flex-1 text-center px-2 py-1.5 bg-gray-100 text-gray-800 rounded text-xs font-semibold hover:bg-gray-200 transition"
                      >
                        Copy coords
                      </button>
                    </div>
                  </div>
                    );
                  })()}
                </Popup>
              </Marker>
            );
          })}

          {/* Driver Markers - Rendered after orders so they appear on top */}
          {showDrivers && onlineDrivers.map(([id, driver]) => {
            if (!driver.latitude || !driver.longitude) return null;

            const activeOrder = orders.find(
              (o) => o.driverId === id &&
                ['accepted', 'in_progress', 'returned_pending'].includes(o.status),
            );

            return (
              <Marker
                key={`driver-${id}`}
                position={[driver.latitude, driver.longitude]}
                icon={driverIcon}
                zIndexOffset={1000}
              >
                <Popup minWidth={260} maxWidth={320}>
                  <div className="p-1" style={{ minWidth: 240 }}>
                    {/* Header: photo + name + online dot */}
                    <div className="flex items-center gap-3 pb-2 border-b border-gray-200">
                      {driver.profilePhoto ? (
                        <img
                          src={driver.profilePhoto}
                          alt={driver.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-green-500"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-lg border-2 border-green-500">
                          {(driver.name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                          <h3 className="font-bold text-gray-800 text-sm truncate">
                            {driver.name || 'Unknown Driver'}
                          </h3>
                        </div>
                        <p className="text-xs text-gray-500">Online</p>
                      </div>
                    </div>

                    {/* Body: key fields */}
                    <div className="pt-2 space-y-1.5 text-xs">
                      {driver.phoneNumber && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500">Phone</span>
                          <a
                            href={`tel:${driver.phoneNumber}`}
                            className="font-mono font-semibold text-blue-600 hover:underline"
                          >
                            {driver.phoneNumber}
                          </a>
                        </div>
                      )}
                      {driver.walletBalance !== null && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500">Wallet</span>
                          <span
                            className={`font-semibold ${
                              driver.walletBalance < 0
                                ? 'text-red-600'
                                : 'text-gray-800'
                            }`}
                          >
                            {Number(driver.walletBalance).toFixed(2)} JOD
                          </span>
                        </div>
                      )}
                      {driver.rating !== null && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500">Rating</span>
                          <span className="font-semibold text-amber-600">
                            ⭐ {driver.rating.toFixed(1)}
                          </span>
                        </div>
                      )}
                      {driver.status && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500">Account</span>
                          <span
                            className={`font-semibold ${
                              driver.status === 'active'
                                ? 'text-green-600'
                                : driver.status === 'banned'
                                ? 'text-red-600'
                                : 'text-gray-700'
                            }`}
                          >
                            {driver.status}
                          </span>
                        </div>
                      )}
                      {driver.lastUpdated && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500">Last update</span>
                          <span className="text-gray-700">
                            {formatTime(driver.lastUpdated)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Active order */}
                    {activeOrder && (
                      <div className="mt-2 p-2 rounded-md bg-yellow-50 border border-yellow-200">
                        <p className="text-[10px] uppercase tracking-wide text-yellow-700 font-bold">
                          Active order
                        </p>
                        <p className="text-xs text-gray-800 mt-0.5">
                          <span className="font-mono font-semibold">
                            #{activeOrder.orderNumber}
                            {activeOrder.subNumber ? `-${activeOrder.subNumber}` : ''}
                          </span>{' '}
                          · {activeOrder.status}
                        </p>
                        {activeOrder.customerName && (
                          <p className="text-xs text-gray-600 truncate">
                            Customer: {activeOrder.customerName}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Location actions */}
                    <div className="mt-2 pt-2 border-t border-gray-200 flex gap-2">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${driver.latitude},${driver.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-2 py-1.5 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 transition"
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg"
                          alt="Google Maps"
                          className="w-5 h-5"
                        />
                        <span className="text-white">Google Map</span>
                      </a>
                      <button
                        type="button"
                        onClick={async (e) => {
                          const coords = `${driver.latitude},${driver.longitude}`;
                          const btn = e.currentTarget;
                          const original = btn.textContent;
                          try {
                            await navigator.clipboard.writeText(coords);
                            btn.textContent = 'Copied!';
                          } catch {
                            // Fallback for older browsers / non-https
                            const ta = document.createElement('textarea');
                            ta.value = coords;
                            document.body.appendChild(ta);
                            ta.select();
                            try { document.execCommand('copy'); } catch (_) {}
                            document.body.removeChild(ta);
                            btn.textContent = 'Copied!';
                          }
                          setTimeout(() => { btn.textContent = original; }, 1500);
                        }}
                        className="flex-1 text-center px-2 py-1.5 bg-gray-100 text-gray-800 rounded text-xs font-semibold hover:bg-gray-200 transition"
                      >
                        Copy coords
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Legend with Statistics */}
        <div className="absolute bottom-6 right-6 bg-white rounded-lg shadow-lg p-4 z-[1000] min-w-[280px]">
          <h4 className="font-semibold text-sm text-gray-800 mb-3 pb-2 border-b">Map Legend</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-gray-700">
              <div className="flex items-center">
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
                  marginRight: '8px'
                }}>
                  <CarFront size={14} color="white" strokeWidth={2.5} />
                </div>
                <span className="font-medium">Online Driver</span>
              </div>
              <span className="font-bold text-green-600 ml-auto min-w-[40px] text-right">{onlineDrivers.length}</span>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-700">
              <div className="flex items-center">
                <div style={{
                  backgroundColor: '#eab308',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(234, 179, 8, 0.3)',
                  marginRight: '8px'
                }}>
                  <Package size={12} color="white" strokeWidth={2.5} />
                </div>
                <span>Waiting</span>
              </div>
              <span className="font-bold text-yellow-600 ml-auto min-w-[40px] text-right">{ordersByStatus.waiting}</span>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-700">
              <div className="flex items-center">
                <div style={{
                  backgroundColor: '#3b82f6',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                  marginRight: '8px'
                }}>
                  <Package size={12} color="white" strokeWidth={2.5} />
                </div>
                <span>Accepted</span>
              </div>
              <span className="font-bold text-blue-600 ml-auto min-w-[40px] text-right">{ordersByStatus.accepted}</span>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-700">
              <div className="flex items-center">
                <div style={{
                  backgroundColor: '#f97316',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(249, 115, 22, 0.3)',
                  marginRight: '8px'
                }}>
                  <Package size={12} color="white" strokeWidth={2.5} />
                </div>
                <span>In Progress</span>
              </div>
              <span className="font-bold text-orange-600 ml-auto min-w-[40px] text-right">{ordersByStatus.in_progress}</span>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-700">
              <div className="flex items-center">
                <div style={{
                  backgroundColor: '#a855f7',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(168, 85, 247, 0.3)',
                  marginRight: '8px'
                }}>
                  <Package size={12} color="white" strokeWidth={2.5} />
                </div>
                <span>Return Pending</span>
              </div>
              <span className="font-bold text-purple-600 ml-auto min-w-[40px] text-right">{ordersByStatus.returned_pending}</span>
            </div>
          </div>
        </div>
      </div >
    </div >
  );
}